import { useSyncExternalStore } from 'react';
import { getBaseThemeColors } from '@nimbalyst/runtime-source/editor/themes/palette';
import type { ExtendedThemeColors } from '@nimbalyst/runtime-source/editor/themes/types';
import crystalDarkTheme from '@nimbalyst/runtime-source/themes/builtin/crystal-dark/theme.json';
import monokaiTheme from '@nimbalyst/runtime-source/themes/builtin/monokai/theme.json';
import solarizedDarkTheme from '@nimbalyst/runtime-source/themes/builtin/solarized-dark/theme.json';
import solarizedLightTheme from '@nimbalyst/runtime-source/themes/builtin/solarized-light/theme.json';

/**
 * The desktop's built-in catalog, in the desktop's order.
 *
 * Every entry is the desktop's own `theme.json`, so a theme cannot mean one
 * thing in the app and another in a browser tab. Light and Dark carry no colour
 * overrides on purpose: their `theme.json` files restate what
 * `getBaseThemeColors` already produces, and reading them from the file as well
 * would give the palette two sources that have to be kept in step. Their names
 * and descriptions still come from the file.
 */
export const THEME_IDS = [
  'light',
  'dark',
  'crystal-dark',
  'monokai',
  'solarized-dark',
  'solarized-light',
] as const;
export type ThemeId = (typeof THEME_IDS)[number];

const STORAGE_KEY = 'nimbalyst-console-theme';

interface ThemeDefinition {
  name: string;
  description: string;
  isDark: boolean;
  colors: Partial<ExtendedThemeColors>;
  /** Material Symbols ligature name, matching the desktop's Themes panel. */
  icon: string;
}

const themeDefinitions = {
  light: { name: 'Light', description: 'Default light theme', isDark: false, colors: {}, icon: 'light_mode' },
  dark: { name: 'Dark', description: 'Default dark theme', isDark: true, colors: {}, icon: 'dark_mode' },
  'crystal-dark': {
    name: crystalDarkTheme.name,
    description: crystalDarkTheme.description,
    isDark: crystalDarkTheme.isDark,
    colors: crystalDarkTheme.colors,
    icon: 'bedtime',
  },
  monokai: {
    name: monokaiTheme.name,
    description: monokaiTheme.description,
    isDark: monokaiTheme.isDark,
    colors: monokaiTheme.colors,
    icon: 'palette',
  },
  'solarized-dark': {
    name: solarizedDarkTheme.name,
    description: solarizedDarkTheme.description,
    isDark: solarizedDarkTheme.isDark,
    colors: solarizedDarkTheme.colors,
    icon: 'palette',
  },
  'solarized-light': {
    name: solarizedLightTheme.name,
    description: solarizedLightTheme.description,
    isDark: solarizedLightTheme.isDark,
    colors: solarizedLightTheme.colors,
    icon: 'palette',
  },
} satisfies Record<ThemeId, ThemeDefinition>;

export interface ThemeCatalogEntry extends ThemeDefinition {
  id: ThemeId;
}

/** What a picker draws: id, label, blurb, icon, and a swatch strip. */
export function themeCatalog(): ThemeCatalogEntry[] {
  return THEME_IDS.map((id) => ({ id, ...themeDefinitions[id] }));
}

/**
 * Three colours that distinguish one theme from another at a glance -- the
 * surface it paints, the surface behind it, and its accent. Resolved through
 * `getThemeColors` so Light and Dark, which carry no overrides, still preview.
 */
export function themeSwatches(theme: ThemeId): string[] {
  const colors = getThemeColors(theme);
  return [colors.bg, colors['bg-secondary'], colors.primary].filter(
    (color): color is string => typeof color === 'string' && color.length > 0,
  );
}

function isThemeId(value: string | null): value is ThemeId {
  return value !== null && THEME_IDS.some((themeId) => themeId === value);
}

function systemPreference(): ThemeId {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function getThemeColors(theme: ThemeId): ExtendedThemeColors {
  const definition = themeDefinitions[theme];
  return {
    ...getBaseThemeColors(definition.isDark),
    ...definition.colors,
  };
}

export function getThemeColorScheme(theme: ThemeId): 'light' | 'dark' {
  return themeDefinitions[theme].isDark ? 'dark' : 'light';
}

/**
 * Reads the stored preference, falling back to the OS setting. Storage access
 * throws in browsers with site data disabled, so treat any failure as "no
 * preference recorded" rather than letting the console fail to render.
 */
export function resolveInitialTheme(): ThemeId {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeId(stored)) return stored;
  } catch {
    // Ignore: fall through to the system preference.
  }
  return systemPreference();
}

export function applyTheme(theme: ThemeId) {
  const root = document.documentElement;
  const isDark = themeDefinitions[theme].isDark;

  // One class per polarity, not per theme: `styles.css` keys its elevation ramp
  // off `.dark-theme`, and every dark theme in the catalog wants that ramp.
  root.classList.remove('dark', 'dark-theme', 'light-theme', 'crystal-dark-theme');
  root.classList.add(isDark ? 'dark-theme' : 'light-theme');
  root.setAttribute('data-theme', theme);

  for (const [key, value] of Object.entries(getThemeColors(theme))) {
    if (value && !key.startsWith('terminal-')) {
      root.style.setProperty(`--nim-${key}`, value);
    }
  }

  root.style.colorScheme = getThemeColorScheme(theme);
}

/*
 * Module-level store rather than per-component state: the theme selector and
 * the wordmark both read the theme, and independent useState copies would let
 * the logo keep the old variant after a selection.
 */
let current: ThemeId = 'light';
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setTheme(theme: ThemeId) {
  if (theme === current) return;
  current = theme;
  applyTheme(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Ignore: the theme still applies for this session.
  }
  listeners.forEach(listener => listener());
}

/** Called once at boot, before React mounts, to avoid a light-theme flash. */
export function initializeTheme() {
  current = resolveInitialTheme();
  applyTheme(current);
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, () => current, () => current);
  return { theme, setTheme };
}
